import type { CancelHandle, UploadParams, UploadProgress } from '../type';

export const DEFAULT_PART_SIZE = 5 * 1024 * 1024;
export const DEFAULT_CONCURRENCY = 4;

/**
 * 校验上传参数中是否包含浏览器 File 对象。
 */
export function assertFile(file?: File): File {
  if (!file) {
    throw new Error('ObjectStorage: file is required');
  }
  return file;
}

/**
 * 校验并规范化云厂商上传使用的 bucket 名称。
 */
export function assertBucketName(bucketName?: string): string {
  const resolved = bucketName?.trim();
  if (!resolved) {
    throw new Error('ObjectStorage: bucketName is required');
  }
  return resolved;
}

/**
 * 解析上传使用的对象 key。
 */
export function resolveUploadFileName(
  params: Pick<UploadParams, 'file' | 'fileName'>
): string {
  const resolved = params.fileName?.trim() || params.file.name?.trim();
  if (!resolved) {
    throw new Error('ObjectStorage: fileName could not be resolved');
  }
  return resolved;
}

/**
 * 判断当前文件是否应使用分片上传。
 */
export function shouldUseMultipartUpload(
  file: File,
  maxSize?: number
): boolean {
  return (
    Number.isFinite(maxSize) &&
    (maxSize as number) > 0 &&
    file.size > (maxSize as number)
  );
}

/**
 * 解析分片大小，并提前拒绝非法值。
 */
export function getUploadPartSize(partSize?: number): number {
  if (partSize == null) {
    return DEFAULT_PART_SIZE;
  }
  if (!Number.isFinite(partSize) || partSize <= 0) {
    throw new Error('ObjectStorage: partSize must be a positive number');
  }
  return Math.floor(partSize);
}

/**
 * 解析分片并发数，并提前拒绝非法值。
 */
export function getUploadConcurrency(concurrency?: number): number {
  if (concurrency == null) {
    return DEFAULT_CONCURRENCY;
  }
  if (!Number.isFinite(concurrency) || concurrency <= 0) {
    throw new Error('ObjectStorage: concurrency must be a positive number');
  }
  return Math.floor(concurrency);
}

/**
 * 创建基于 XHR 的取消句柄，并立即暴露给调用方。
 */
export function createCancelHandle(
  onCancel?: (handle: CancelHandle) => void
): CancelHandle {
  const handle: CancelHandle = {
    instance: null,
    cancel() {
      handle.instance?.abort();
    },
  };
  onCancel?.(handle);
  return handle;
}

/**
 * 为 normal 分片上传生成客户端侧批次 id。
 */
export function createMultipartUploadId(): string {
  return (
    globalThis.crypto?.randomUUID?.() ??
    `${Date.now()}-${Math.floor(Math.random() * 1_000_000)}`
  );
}

/**
 * 将进度值规范化为稳定的公开结构。
 */
export function createProgressSnapshot(
  loaded: number,
  total: number
): UploadProgress {
  const safeLoaded = Math.max(Math.floor(loaded), 0);
  const safeTotal = Math.max(Math.floor(total), 0);
  const normalizedLoaded =
    safeTotal > 0 ? Math.min(safeLoaded, safeTotal) : safeLoaded;

  return {
    loaded: normalizedLoaded,
    total: safeTotal,
    percent:
      safeTotal > 0
        ? Number(((normalizedLoaded / safeTotal) * 100).toFixed(2))
        : 0,
  };
}

/**
 * 按指定并发数运行异步任务。
 */
export async function runWithConcurrency<T>(
  items: T[],
  concurrency: number,
  worker: (item: T, index: number) => Promise<void>
): Promise<void> {
  if (items.length === 0) return;

  let cursor = 0;

  async function consume(): Promise<void> {
    while (cursor < items.length) {
      const index = cursor++;
      await worker(items[index], index);
    }
  }

  const runners = Array.from(
    { length: Math.min(Math.max(concurrency, 1), items.length) },
    () => consume()
  );

  await Promise.all(runners);
}
